
import React, { useEffect, ReactNode } from 'react';
import { motion, useAnimation, Variant } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface AnimatedElementProps {
  children: ReactNode;
  animation?: 'fadeIn' | 'slideUp' | 'slideLeft' | 'slideRight' | 'scale' | 'bounce' | 'flip' | 'rotate' | 'stagger';
  delay?: number;
  duration?: number;
  className?: string;
  once?: boolean;
  threshold?: number;
  staggerChildren?: number;
  staggerDirection?: 1 | -1;
}

const variants = {
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  },
  slideUp: {
    hidden: { y: 50, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  },
  slideLeft: {
    hidden: { x: 50, opacity: 0 },
    visible: { x: 0, opacity: 1 }
  },
  slideRight: {
    hidden: { x: -50, opacity: 0 },
    visible: { x: 0, opacity: 1 }
  },
  scale: {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { scale: 1, opacity: 1 }
  },
  bounce: {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15
      }
    }
  },
  flip: {
    hidden: { rotateX: 90, opacity: 0 },
    visible: { rotateX: 0, opacity: 1 }
  },
  rotate: {
    hidden: { rotate: -15, opacity: 0, scale: 0.9 },
    visible: { rotate: 0, opacity: 1, scale: 1 }
  },
  stagger: {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }
};

const AnimatedElement: React.FC<AnimatedElementProps> = ({
  children,
  animation = 'fadeIn',
  delay = 0,
  duration = 0.6,
  className = '',
  once = true,
  threshold = 0.1,
  staggerChildren = 0.1,
  staggerDirection = 1
}) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: once,
    threshold: window.innerWidth < 768 ? 0.05 : threshold, // Lower threshold on mobile
    // Higher rootMargin on mobile for earlier animation triggering
    rootMargin: window.innerWidth < 768 ? "0px 0px -10px 0px" : "0px"
  });

  useEffect(() => {
    if (inView) {
      // Start animation with reduced delay on mobile
      const mobileAdjustedDelay = window.innerWidth < 768 ? Math.max(0.05, delay / 3) : delay;
      controls.start('visible', { delay: mobileAdjustedDelay });
    } else if (!once) {
      controls.start('hidden');
    }
  }, [controls, inView, once, delay]);

  // Create custom transition object based on animation type
  const getTransition = () => {
    // Adjust animation speed on mobile - make it faster
    const mobileAdjustedDuration = window.innerWidth < 768 ? Math.max(0.2, duration * 0.7) : duration;
    
    if (animation === 'bounce') {
      return { 
        delay, 
        type: "spring", 
        stiffness: window.innerWidth < 768 ? 280 : 300, 
        damping: window.innerWidth < 768 ? 18 : 15 
      };
    }
    if (animation === 'stagger') {
      return { 
        delay, 
        staggerChildren: window.innerWidth < 768 ? Math.max(0.03, staggerChildren / 2) : staggerChildren,
        staggerDirection
      };
    }
    return { 
      duration: mobileAdjustedDuration, 
      delay, 
      ease: [0.22, 1, 0.36, 1] 
    };
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variants[animation]}
      transition={getTransition()}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default AnimatedElement;
